import React, { ChangeEvent, useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Switch,
  FormControlLabel,
} from "@mui/material";
import Tool, { SharePointTool as SharePointToolType } from "../../../Shared-ui/Types/Tool";
import { useDispatch } from "react-redux";
import {
  StartLoading,
  StopLoading,
} from "../../../Shared-ui/Store/LoadingReducer/LoaderActions";
import BaseInstance from "../../../Services/AxiosInstance/BaseInstance";
import { TOOLKIT, TOOLS } from "../../../Services/Endpoint";
import { ShowError } from "../../../Shared-ui/Store/MessageReducer/MsgActions";
import { ToolsKitTypeEnum } from "../../../Shared-ui/Enum/ToolsEnum";

interface SharePointToolProps {
  handleClose: () => void;
  tool: SharePointToolType | null;
}

const SharePointTool: React.FC<SharePointToolProps> = ({ handleClose, tool }) => {
  const toolInitial: SharePointToolType = {
    name: "",
    description: "",
    type: "toolkit",
    is_active: true,
    config: {
      toolkit_type: ToolsKitTypeEnum.SharePoint,
      client_id: "",
      tenant_id: "",
      x5t_value: "",
      private_key: "",
      scope: "",
    //   site: "",
    },
  };

  const [tools, setTools] = useState<SharePointToolType>(toolInitial);
  const [isEditing, setIsEditing] = useState(false);
  const dispatch = useDispatch();
  const [disableSubmit, setDisableSubmit] = useState<boolean>(true);

  useEffect(() => {
    // Check if all required fields are filled
    const requiredFieldsFilled =
      !!tools.name.trim() &&
      !!tools.description.trim() &&
      !!tools.config.client_id.trim() &&
      !!tools.config.tenant_id.trim() &&
      !!tools.config.x5t_value.trim() &&
      !!tools.config.private_key.trim() &&
      !!tools.config.scope.trim();

    setDisableSubmit(!requiredFieldsFilled);
  }, [tools]);

  useEffect(() => {
    if (tool) {
      setTools(tool);
      setIsEditing(true);
    }
  }, [tool]);

  const handleCreateTool = async () => {
    try {
      dispatch(StartLoading());
      
      // Log the original private key
      console.log("Original private key:", tools.config.private_key);
      
      // Format the private key: preserve PEM structure with proper \n placement
      let formattedPrivateKey = tools.config.private_key
        .replace(/\r?\n/g, '')  // Remove all actual line breaks first
        .replace(/-----BEGIN PRIVATE KEY-----/, '-----BEGIN PRIVATE KEY-----\\n')  // Add \n after header
        .replace(/-----END PRIVATE KEY-----/, '\\n-----END PRIVATE KEY-----')  // Add \n before footer
        .replace(/\s+/g, '\\n')  // Convert remaining spaces to \n
        .replace(/-----BEGIN\\nPRIVATE\\nKEY-----/g, '-----BEGIN PRIVATE KEY-----')  // Fix header
        .replace(/-----END\\nPRIVATE\\nKEY-----/g, '-----END PRIVATE KEY-----')  // Fix footer
        .replace(/\\n\\n+/g, '\\n') // Remove multiple consecutive \n
        .replace(/^\\n|\\n$/g, ''); // Remove leading/trailing \n
      
      // Log the formatted private key
      console.log("Formatted private key:", formattedPrivateKey);
      
      const formattedTools = {
        ...tools,
        config: {
          ...tools.config,
          private_key: formattedPrivateKey
        }
      };
      console.log("Formatted data:", formattedTools); // Debug log
      const data = await BaseInstance.post(TOOLKIT, formattedTools).then(
        (res) => res?.data
      );
      handleCancel();
    } catch (err: any) {
      dispatch(
        ShowError(
          err?.response?.data?.detail || err?.response?.data?.statusText
        )
      );
    } finally {
      dispatch(StopLoading());
    }
  };

  const handleUpdateTool = async () => {
    try {
      dispatch(StartLoading());
      
      // Log the original private key
      console.log("Original private key:", tools.config.private_key);
      
      // Format the private key: preserve PEM structure with proper \n placement
      let formattedPrivateKey = tools.config.private_key
        .replace(/\r?\n/g, '')  // Remove all actual line breaks first
        .replace(/-----BEGIN PRIVATE KEY-----/, '-----BEGIN PRIVATE KEY-----\\n')  // Add \n after header
        .replace(/-----END PRIVATE KEY-----/, '\\n-----END PRIVATE KEY-----')  // Add \n before footer
        .replace(/\s+/g, '\\n')  // Convert remaining spaces to \n
        .replace(/-----BEGIN\\nPRIVATE\\nKEY-----/g, '-----BEGIN PRIVATE KEY-----')  // Fix header
        .replace(/-----END\\nPRIVATE\\nKEY-----/g, '-----END PRIVATE KEY-----')  // Fix footer
        .replace(/\\n\\n+/g, '\\n') // Remove multiple consecutive \n
        .replace(/^\\n|\\n$/g, ''); // Remove leading/trailing \n
      
      // Log the formatted private key
      console.log("Formatted private key:", formattedPrivateKey);
      
      const formattedTools = {
        ...tools,
        config: {
          ...tools.config,
          private_key: formattedPrivateKey
        }
      };
      console.log("Formatted data:", formattedTools); // Debug log
      const data = await BaseInstance.put(`${TOOLS}/${tools.id}`, formattedTools).then(
        (res) => res?.data
      );
      handleCancel();
    } catch (err: any) {
      dispatch(
        ShowError(
          err?.response?.data?.detail || err?.response?.data?.statusText
        )
      );
    } finally {
      dispatch(StopLoading());
    }
  };

  const handleCancel = () => {
    setTools(toolInitial);
    handleClose();
  };

  const handleChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setTools((preVal) => ({ ...preVal, [name]: value }));
  };

  const handleChangeConfig = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    console.log(`Field: ${name}, Value: ${value}`); // Add this line
    setTools((preVal) => ({
      ...preVal,
      config: { ...preVal.config, [name]: value },
    }));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, py: 1 }}>
      <TextField
        fullWidth
        label="Tool Name"
        value={tools.name}
        name="name"
        onChange={handleChange}
        variant="outlined"
        inputProps={{ maxLength: 40 }}
      />

      <TextField
        fullWidth
        label="Description"
        value={tools.description}
        name="description"
        onChange={handleChange}
        variant="outlined"
        multiline
        rows={2}
        inputProps={{ maxLength: 255 }}
      />

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Client ID"
            name="client_id"
            value={tools.config.client_id}
            onChange={handleChangeConfig}
            variant="outlined"
            placeholder="Azure App Registration Client ID"
            inputProps={{ maxLength: 255 }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Tenant ID"
            name="tenant_id"
            value={tools.config.tenant_id}
            onChange={handleChangeConfig}
            variant="outlined"
            placeholder="Azure Tenant ID"
            inputProps={{ maxLength: 255 }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="X5T Value"
            name="x5t_value"
            value={tools.config.x5t_value}
            onChange={handleChangeConfig}
            variant="outlined"
            placeholder="Certificate thumbprint"
            inputProps={{ maxLength: 255 }}
          />
        </Grid>
        {/* <Grid item xs={6}>
          <TextField
            fullWidth
            label="Site"
            name="site"
            value={tools.config.site}
            onChange={handleChangeConfig}
            variant="outlined"
            placeholder="SharePoint site name (optional)"
            inputProps={{ maxLength: 255 }}
          />
        </Grid> */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Scope"
            name="scope"
            value={tools.config.scope}
            onChange={handleChangeConfig}
            variant="outlined"
            placeholder="https://yourdomain.sharepoint.com"
            inputProps={{ maxLength: 255 }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Private Key"
            name="private_key"
            value={tools.config.private_key}
            onChange={handleChangeConfig}
            variant="outlined"
            multiline
            rows={4}
            placeholder="Paste your private key here (PEM format)"
          />
        </Grid>
      </Grid>

      <Typography variant="body1" gutterBottom>
        Configuration of SharePoint
      </Typography>

      <FormControlLabel
        control={
          <Switch
            checked={tools.is_active}
            name="is_active"
            onChange={(e) =>
              setTools((preVal) => ({
                ...preVal,
                [e.target.name]: e.target.checked,
              }))
            }
          />
        }
        label="Active"
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          mb: 3,
        }}
      >
        <Button
          variant="outlined"
          size="large"
          sx={{ textTransform: "capitalize" }}
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          disabled={disableSubmit}
          size="large"
          sx={{ textTransform: "capitalize" }}
          onClick={() => (isEditing ? handleUpdateTool() : handleCreateTool())}
        >
          {isEditing ? "Update" : "Create"}
        </Button>
      </Box>
    </Box>
  );
};

export default SharePointTool; 